// src/utils/pdfCartaFornitore.js
// ═══════════════════════════════════════════════════════════════════════════
// DOCUMENTI PDF SULLA CARTA INTESTATA DEL FORNITORE (W431) — offerte di formazione e proforma
// ═══════════════════════════════════════════════════════════════════════════
// Un solo modello per i documenti che il Platform Owner manda ai clienti: intestazione da
// «Dati del fornitore» + logo, «Spett.le», oggetto, voci, totali con IVA, un riquadro di
// spiegazione, il pagamento con IBAN, le condizioni e il piè di pagina.
// Senza numero il documento esce come BOZZA (anteprima: nessun numero consumato).
// Riceve il costruttore jsPDF: la stessa funzione gira nell'app e nelle prove.
// ═══════════════════════════════════════════════════════════════════════════
const VIOLA = [91, 33, 182], SCURO = [30, 27, 75], GRIGIO = [107, 114, 128], LINEA = [229, 231, 235];
export const euroPdf = (n) => "€ " + (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dataIt = (s) => { const [a, m, g] = String(s || "").split("-"); return a && m && g ? `${g}/${m}/${a}` : ""; };

/**
 * @param d { titolo ("OFFERTA" | "PROFORMA"), numero, dataEmissione, fornitore, banca: { logo },
 *   cliente: { ragioneSociale, indirizzo, piva, referente, email }, oggetto, intro: [testo],
 *   voci: [{ descrizione, dettaglio, importo }], imponibile, iva, ivaPerc, totale,
 *   riquadro: { titolo, righe } | null, pagamento: [testo], condizioni: [testo], avviso: testo | null, firma: bool }
 */
export function disegnaDocumento(JsPDF, d) {
  const f = d.fornitore || {}, b = d.banca || {}, c = d.cliente || {};
  const doc = new JsPDF({ unit: "mm", format: "a4" });
  const L = 18, R = 192, W = R - L;
  let y = 16;
  const testo = (t, x, yy, opz = {}) => { doc.text(String(t ?? ""), x, yy, opz); };
  const font = (stile = "normal", size = 10, colore = [17, 24, 39]) => { doc.setFont("helvetica", stile); doc.setFontSize(size); doc.setTextColor(...colore); };
  const aCapo = (t, larg) => doc.splitTextToSize(String(t ?? ""), larg);
  const nuovaPaginaSeServe = (h) => { if (y + h > 283) { doc.addPage(); y = 18; } };

  // intestazione
  let xTesto = L;
  if (b.logo && /^data:image\/(png|jpe?g);base64,/.test(b.logo)) {
    try {
      const p = doc.getImageProperties(b.logo);
      const w = Math.min(46, (p.width / p.height) * 16);
      doc.addImage(b.logo, b.logo.includes("png") ? "PNG" : "JPEG", L, y - 4, w, w / (p.width / p.height));
      xTesto = L + w + 5;
    } catch { /* logo non leggibile: solo testo */ }
  }
  font("bold", 13, SCURO); testo(f.ragioneSociale || "Nexiora", xTesto, y + 1);
  font("normal", 8.5, GRIGIO);
  const righeF = [f.sedeLegale, [f.piva ? `P.IVA ${f.piva}` : "", f.codiceFiscale && f.codiceFiscale !== f.piva ? `C.F. ${f.codiceFiscale}` : ""].filter(Boolean).join(" · "),
    [f.telefono, f.email, f.pec ? `PEC ${f.pec}` : ""].filter(Boolean).join(" · "), f.sitoWeb].filter(Boolean);
  righeF.forEach((r, i) => testo(r, xTesto, y + 6 + i * 4));
  font("bold", 10, VIOLA); testo(d.titolo || "DOCUMENTO", R, y, { align: "right" });
  font("bold", 14, d.numero ? VIOLA : GRIGIO); testo(d.numero || "BOZZA", R, y + 7, { align: "right" });
  font("normal", 9, GRIGIO); testo(`Data: ${dataIt(d.dataEmissione) || "—"}`, R, y + 12, { align: "right" });
  y = Math.max(y + 8 + righeF.length * 4, y + 18);
  doc.setDrawColor(...VIOLA); doc.setLineWidth(0.8); doc.line(L, y, R, y); y += 7;
  if (!d.numero) { font("bold", 8.5, [180, 83, 9]); testo("ANTEPRIMA — documento non ancora emesso", L, y - 2.5); }

  // destinatario
  font("normal", 8.5, GRIGIO); testo("Spett.le", 118, y);
  font("bold", 10.5, [17, 24, 39]); let yc = y + 5;
  aCapo(c.ragioneSociale || "", 74).forEach(r => { testo(r, 118, yc); yc += 4.6; });
  font("normal", 9, [55, 65, 81]);
  [c.indirizzo, c.piva ? `P.IVA ${c.piva}` : "", c.referente ? `Alla c.a. di ${c.referente}` : "", c.email].filter(Boolean)
    .forEach(r => aCapo(r, 74).forEach(rr => { testo(rr, 118, yc); yc += 4.2; }));
  y = Math.max(yc, y + 20) + 3;

  // oggetto e testo
  font("bold", 10.5, SCURO);
  aCapo(`Oggetto: ${d.oggetto || ""}`, W).forEach(r => { testo(r, L, y); y += 5; });
  y += 1;
  font("normal", 9.5, [55, 65, 81]);
  (d.intro || []).filter(Boolean).forEach(par => { aCapo(par, W).forEach(r => { testo(r, L, y); y += 4.6; }); y += 1; });
  y += 3;

  // voci
  doc.setFillColor(245, 243, 255); doc.rect(L, y - 4.5, W, 7, "F");
  font("bold", 9, SCURO); testo("Descrizione", L + 2, y); testo("Importo", R - 2, y, { align: "right" }); y += 6;
  (d.voci || []).forEach(v => {
    const righe = aCapo(v.descrizione, W - 40), det = v.dettaglio ? aCapo(v.dettaglio, W - 40) : [];
    nuovaPaginaSeServe(6 + (righe.length + det.length) * 4.5);
    font("normal", 9.5, [17, 24, 39]); righe.forEach((r, i) => testo(r, L + 2, y + i * 4.6));
    testo(euroPdf(v.importo), R - 2, y, { align: "right" });
    let yy = y + righe.length * 4.6;
    font("normal", 8, GRIGIO); det.forEach(r => { testo(r, L + 2, yy - 0.6); yy += 3.8; });
    y = yy + 1; doc.setDrawColor(...LINEA); doc.setLineWidth(0.2); doc.line(L, y - 2.5, R, y - 2.5); y += 2;
  });
  nuovaPaginaSeServe(22);
  const riga = (et, val, forte) => { font(forte ? "bold" : "normal", forte ? 11 : 9.5, forte ? SCURO : [55, 65, 81]); testo(et, 150, y, { align: "right" }); testo(val, R - 2, y, { align: "right" }); y += forte ? 6 : 5; };
  riga("Imponibile", euroPdf(d.imponibile)); riga(`IVA ${d.ivaPerc ?? 22}%`, euroPdf(d.iva)); riga("Totale", euroPdf(d.totale), true);
  y += 3;

  // riquadro di spiegazione
  if (d.riquadro && (d.riquadro.righe || []).length) {
    const righe = d.riquadro.righe.flatMap(t => aCapo("• " + t, W - 8));
    const h = 9 + righe.length * 4.4;
    nuovaPaginaSeServe(h + 4);
    doc.setFillColor(255, 251, 235); doc.setDrawColor(253, 230, 138); doc.setLineWidth(0.3); doc.roundedRect(L, y - 4, W, h, 2, 2, "FD");
    font("bold", 9.5, [146, 64, 14]); testo(d.riquadro.titolo, L + 4, y + 1); let yy = y + 6;
    font("normal", 8.8, [120, 53, 15]); righe.forEach(r => { testo(r, L + 4, yy); yy += 4.4; });
    y += h + 4;
  }

  // pagamento
  const righeP = (d.pagamento || []).filter(Boolean).flatMap(t => aCapo(t, W - 8));
  if (righeP.length) {
    nuovaPaginaSeServe(10 + righeP.length * 4.6);
    doc.setFillColor(245, 243, 255); doc.setDrawColor(221, 214, 254); doc.roundedRect(L, y - 4, W, 8 + righeP.length * 4.6, 2, 2, "FD");
    font("bold", 9.5, SCURO); testo("Pagamento", L + 4, y + 1); let yp = y + 6;
    font("normal", 9.2, [17, 24, 39]); righeP.forEach(r => { testo(r, L + 4, yp); yp += 4.6; });
    y = yp + 5;
  }

  // condizioni
  const cond = (d.condizioni || []).flatMap(t => aCapo("• " + t, W));
  if (cond.length) {
    nuovaPaginaSeServe(8 + cond.length * 4);
    font("bold", 9, SCURO); testo("Condizioni", L, y); y += 4.6;
    font("normal", 8.3, GRIGIO); cond.forEach(r => { testo(r, L, y); y += 3.9; });
    y += 3;
  }
  if (d.avviso) { nuovaPaginaSeServe(8); font("bold", 8.5, [146, 64, 14]); aCapo(d.avviso, W).forEach(r => { testo(r, L, y); y += 4; }); y += 2; }
  if (d.firma) {
    nuovaPaginaSeServe(12);
    font("normal", 9, [55, 65, 81]); testo("Per accettazione (timbro e firma del cliente)", L, y); testo(f.ragioneSociale || "", R, y, { align: "right" });
    doc.setDrawColor(...GRIGIO); doc.setLineWidth(0.2); doc.line(L, y + 10, L + 70, y + 10);
  }

  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i); font("normal", 7.5, GRIGIO);
    testo([f.ragioneSociale, f.piva ? `P.IVA ${f.piva}` : ""].filter(Boolean).join(" · "), L, 289);
    testo(`${d.numero || "Bozza"} · pagina ${i} di ${n}`, R, 289, { align: "right" });
  }
  return doc;
}
